import type { PhotoDayReason } from "./model";

// Pure selection logic shared by the Worker ("photo of the day") and the
// slideshow (shuffle). Deterministic, so every screen in the house agrees.

/** 32-bit FNV-1a hash of a string. */
export function fnv1a(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Small seeded PRNG (mulberry32); returns floats in [0, 1). */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Fisher–Yates shuffle with a seed; does not mutate the input. */
export function seededShuffle<T>(items: readonly T[], seed: string): T[] {
  const out = items.slice();
  const rand = seededRandom(fnv1a(seed));
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/**
 * Stable daily choice: the item whose hash with the date is smallest. Unlike
 * "index = hash % length", adding or removing other photos only changes the
 * pick if the added photo wins or the chosen one is removed.
 */
export function dailyPick<T extends { id: string }>(items: readonly T[], date: string): T | null {
  let best: T | null = null;
  let bestScore = Infinity;
  for (const it of items) {
    const score = fnv1a(`${date}|${it.id}`);
    if (score < bestScore || (score === bestScore && best && it.id < best.id)) {
      best = it;
      bestScore = score;
    }
  }
  return best;
}

function isLeap(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

/**
 * Photos taken on the same month and day as `date` ("YYYY-MM-DD") in an
 * earlier year. On Feb 28 of a non-leap year, Feb 29 photos count too.
 */
export function onThisDay<T extends { takenAt: string | null }>(items: readonly T[], date: string): { item: T; yearsAgo: number }[] {
  const y = Number(date.slice(0, 4));
  const md = date.slice(5, 10);
  const alsoLeapDay = md === "02-28" && !isLeap(y);
  const out: { item: T; yearsAgo: number }[] = [];
  for (const item of items) {
    if (!item.takenAt) continue;
    const ty = Number(item.takenAt.slice(0, 4));
    const tmd = item.takenAt.slice(5, 10);
    if (ty >= y) continue;
    if (tmd === md || (alsoLeapDay && tmd === "02-29")) out.push({ item, yearsAgo: y - ty });
  }
  return out;
}

/**
 * The single photo for a "photo of the day" card: an "On this day" memory
 * when one exists (chosen stably among them), otherwise a stable daily pick.
 */
export function pickPhotoOfDay<T extends { id: string; takenAt: string | null }>(
  items: readonly T[],
  date: string,
): { item: T; reason: PhotoDayReason; yearsAgo: number | null } | null {
  const memories = onThisDay(items, date);
  if (memories.length) {
    const chosen = dailyPick(
      memories.map((m) => ({ id: m.item.id, m })),
      date,
    )!.m;
    return { item: chosen.item, reason: "onThisDay", yearsAgo: chosen.yearsAgo };
  }
  const item = dailyPick(items, date);
  return item ? { item, reason: "dailyPick", yearsAgo: null } : null;
}

export function yearsAgoLabel(n: number): string {
  return n === 1 ? "1 year ago today" : `${n} years ago today`;
}
